/**
 * Cohort API Service
 * Handles all cohort-related API calls
 */

import { apiGet, apiPost, apiPatch, apiDelete } from "./api";
import type {
  ApiCohort,
  ApiCohortMember,
  ApiCohortInviteInfo,
  ApiCohortEnrollResponse,
  Cohort,
  CohortMember,
  CohortInviteInfo,
  CohortEnrollResponse,
  CohortCreateRequest,
  CohortUpdateRequest,
  CohortMemberBulkCreateRequest,
  CohortEnrollRequest,
  GoogleGroupSyncResult,
  CohortStatus,
  CohortMemberStatus,
} from "@/types/cohorts";
import {
  transformCohort,
  transformCohortMember,
  transformCohortInviteInfo,
  transformCohortEnrollResponse,
} from "@/types/cohorts";

// Query parameters for listing cohorts
export interface CohortListParams {
  skip?: number;
  limit?: number;
  status?: CohortStatus;
  workshop_id?: string;
}

// Query parameters for listing members
export interface CohortMemberListParams {
  skip?: number;
  limit?: number;
  status?: CohortMemberStatus;
}

/**
 * Get all cohorts with optional filters
 */
export async function getCohorts(params?: CohortListParams): Promise<Cohort[]> {
  const apiCohorts = await apiGet<ApiCohort[]>("/cohorts", params as Record<string, unknown>);
  return apiCohorts.map(transformCohort);
}

/**
 * Get a single cohort by ID
 */
export async function getCohort(cohortId: string): Promise<Cohort> {
  const apiCohort = await apiGet<ApiCohort>(`/cohorts/${cohortId}`);
  return transformCohort(apiCohort);
}

/**
 * Create a new cohort
 */
export async function createCohort(data: CohortCreateRequest): Promise<Cohort> {
  const apiCohort = await apiPost<ApiCohort>("/cohorts", data);
  return transformCohort(apiCohort);
}

/**
 * Update an existing cohort
 */
export async function updateCohort(
  cohortId: string,
  data: CohortUpdateRequest
): Promise<Cohort> {
  const apiCohort = await apiPatch<ApiCohort>(`/cohorts/${cohortId}`, data);
  return transformCohort(apiCohort);
}

/**
 * Delete a cohort
 */
export async function deleteCohort(cohortId: string): Promise<void> {
  await apiDelete(`/cohorts/${cohortId}`);
}

/**
 * Get members of a cohort
 */
export async function getCohortMembers(
  cohortId: string,
  params?: CohortMemberListParams
): Promise<CohortMember[]> {
  const apiMembers = await apiGet<ApiCohortMember[]>(
    `/cohorts/${cohortId}/members`,
    params as Record<string, unknown>
  );
  return apiMembers.map(transformCohortMember);
}

/**
 * Add members to a cohort (bulk)
 */
export async function addCohortMembers(
  cohortId: string,
  data: CohortMemberBulkCreateRequest
): Promise<CohortMember[]> {
  const apiMembers = await apiPost<ApiCohortMember[]>(
    `/cohorts/${cohortId}/members`,
    data
  );
  return apiMembers.map(transformCohortMember);
}

/**
 * Remove a member from a cohort
 */
export async function removeCohortMember(
  cohortId: string,
  memberId: string
): Promise<void> {
  await apiDelete(`/cohorts/${cohortId}/members/${memberId}`);
}

/**
 * Sync cohort members with Google Group
 */
export async function syncGoogleGroup(
  cohortId: string
): Promise<GoogleGroupSyncResult> {
  return apiPost<GoogleGroupSyncResult>(`/cohorts/${cohortId}/sync`);
}

/**
 * Get cohort info by invite code (public)
 */
export async function getCohortByInviteCode(
  inviteCode: string
): Promise<CohortInviteInfo> {
  const apiInfo = await apiGet<ApiCohortInviteInfo>(
    `/cohorts/invite/${inviteCode}`
  );
  return transformCohortInviteInfo(apiInfo);
}

/**
 * Self-enroll in a cohort using invite code (public)
 */
export async function enrollInCohort(
  inviteCode: string,
  data: CohortEnrollRequest
): Promise<CohortEnrollResponse> {
  const apiResponse = await apiPost<ApiCohortEnrollResponse>(
    `/cohorts/enroll/${inviteCode}`,
    data
  );
  return transformCohortEnrollResponse(apiResponse);
}

/**
 * Generate invite link for a cohort
 */
export function generateInviteLink(inviteCode: string): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/enroll/${inviteCode}`;
}
